import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { Archive, Build, Close, ContentCopy, Unarchive } from "@mui/icons-material";
import { phappsRequest } from "@/services/apiService";
import { PHARMACY_HALO_COLORS } from "@/config/colors";
import { buildCherubSessionMessagesPath, buildCherubSessionTicketsPath } from "@/config/apiConstants";

const PH_BLUE   = PHARMACY_HALO_COLORS.BLUE;
const PH_YELLOW = PHARMACY_HALO_COLORS.YELLOW;
const PH_YELLOW_TINT = "#FFF8E7";
const PH_YELLOW_LIGHT = "#FFE8A3";

const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("en-AU", { timeStyle: "short" }) : "";

const TICKET_TYPE_LABELS = { callback: "Callback", bug: "Bug report", training: "Training" };
const TICKET_STATUS_STYLES = {
  open:        { bg: "#fef9c3", color: "#78350f" },
  in_progress: { bg: "#dbeafe", color: "#1d4ed8" },
  resolved:    { bg: "#dcfce7", color: "#16a34a" },
};

const MessageBubble = ({ msg, staffName, chemistName }) => {
  const isUser = msg.Role === "user";

  const toolNames = msg.ToolsUsed
    ? msg.ToolsUsed.split(",").map((t) => t.trim()).filter(Boolean)
    : [];

  const tokenInfo =
    msg.Role === "assistant" && (msg.InputTokens || msg.OutputTokens)
      ? [
          msg.InputTokens  ? `${Number(msg.InputTokens).toLocaleString()} in`     : null,
          msg.CacheReadTokens ? `${Number(msg.CacheReadTokens).toLocaleString()} cached` : null,
          msg.OutputTokens ? `${Number(msg.OutputTokens).toLocaleString()} out`   : null,
        ].filter(Boolean).join(" · ")
      : null;

  const senderLabel = isUser
    ? [staffName, chemistName].filter(Boolean).join(" · ")
    : "Cherub";

  const showSentimentBadge = isUser && msg.SentimentScore != null && msg.SentimentScore !== 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: isUser ? "flex-end" : "flex-start",
        mb: 2,
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, mb: 0.4, mx: 0.75, color: isUser ? PH_BLUE : PH_YELLOW, letterSpacing: "0.01em" }}
      >
        {senderLabel}
      </Typography>

      <Box sx={{ maxWidth: "78%", display: "flex", flexDirection: "column", alignItems: isUser ? "flex-end" : "flex-start" }}>
        <Paper
          elevation={0}
          sx={{
            px: 2, py: 1.25,
            borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
            backgroundColor: isUser ? PH_BLUE : "#ffffff",
            color: isUser ? "white" : "#1e293b",
            fontSize: 14, lineHeight: 1.65,
            wordBreak: "break-word", whiteSpace: "pre-wrap",
            border: isUser ? "none" : `1.5px solid ${PH_YELLOW_LIGHT}`,
            boxShadow: isUser ? "0 2px 8px rgba(0,33,105,0.18)" : "0 1px 4px rgba(0,0,0,0.07)",
          }}
        >
          {msg.MessageText}
        </Paper>

        {toolNames.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
            <Build sx={{ fontSize: 12, color: PH_BLUE, opacity: 0.7 }} />
            {toolNames.map((name) => (
              <Chip
                key={name}
                label={name.replace(/_/g, " ")}
                size="small"
                sx={{ fontSize: 11, height: 20, backgroundColor: PH_YELLOW_LIGHT, color: PH_BLUE, fontWeight: 700, "& .MuiChip-label": { px: 1 } }}
              />
            ))}
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "center", mt: 0.3, mx: 0.75, gap: 0.75, flexWrap: "wrap" }}>
          <Typography variant="caption" sx={{ color: "#94a3b8" }}>
            {fmtTime(msg.CreatedAt)}
            {tokenInfo && (
              <Tooltip title="Token usage for this response" placement="top">
                <span style={{ marginLeft: 6, cursor: "default" }}>· {tokenInfo}</span>
              </Tooltip>
            )}
          </Typography>
          {showSentimentBadge && (
            <Chip
              label={msg.SentimentScore === 1 ? "Satisfied" : "Frustrated"}
              size="small"
              sx={{
                height: 18, fontSize: 10, fontWeight: 700,
                backgroundColor: msg.SentimentScore === 1 ? "#dcfce7" : "#fee2e2",
                color: msg.SentimentScore === 1 ? "#16a34a" : "#dc2626",
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const CherubSessionModal = ({ session, onClose, onAction, isArchived = false }) => {
  const [messages, setMessages] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    setLoading(true);
    setMessages([]);
    setTickets([]);
    setError(null);

    Promise.all([
      phappsRequest(buildCherubSessionMessagesPath(session.SessionID), "GET"),
      phappsRequest(buildCherubSessionTicketsPath(session.SessionID), "GET").catch(() => ({ tickets: [] })),
    ]).then(([msgRes, ticketRes]) => {
      if (!cancelled) {
        setMessages(msgRes?.messages || []);
        setTickets(ticketRes?.tickets || []);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setError("Failed to load conversation messages.");
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [session?.SessionID]);

  useEffect(() => {
    if (!loading && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [loading, messages]);

  const copyTranscript = () => {
    const divider = "─".repeat(50);
    const lines = [
      `Cherub Conversation — ${session?.ChemistName || "Unknown"} / ${session?.StaffName || "Unknown"}`,
      `${session?.App || ""} → ${session?.AppSection || ""} | ${session?.StartedAt ? new Date(session.StartedAt).toLocaleString("en-AU") : ""}`,
      divider,
      ...messages.map((m) => {
        const t = fmtTime(m.CreatedAt);
        const sender = m.Role === "user" ? (session?.StaffName || "Staff") : "Cherub";
        return `${sender} (${t}): ${m.MessageText}`;
      }),
      divider,
      `Session summary: ${session?.Summary || "(No summary yet)"}`,
    ];
    navigator.clipboard.writeText(lines.join("\n")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  const topics = Array.isArray(session?.SummaryTopics) ? session.SummaryTopics : [];
  const startedDate = session?.StartedAt
    ? new Date(session.StartedAt).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })
    : "—";
  const endedTime = session?.EndedAt
    ? new Date(session.EndedAt).toLocaleTimeString("en-AU", { timeStyle: "short" })
    : null;
  const totalTokens = (session?.TotalInputTokens || 0) + (session?.TotalOutputTokens || 0);
  const hasSummaryContent = session?.Summary || topics.length > 0;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: "85vh", display: "flex", flexDirection: "column", borderRadius: 4, overflow: "hidden" },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ backgroundColor: PH_BLUE, pb: 1.5, pt: 2, pr: 9 }}>
        <Typography fontWeight={700} sx={{ color: PH_YELLOW, fontSize: "1.1rem", lineHeight: 1.3 }}>
          {session?.ChemistName || "Unknown Chemist"}
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", mt: 0.25 }}>
          {[
            session?.StaffName,
            [session?.App, session?.AppSection].filter(Boolean).join(" / "),
            [startedDate, endedTime ? `– ${endedTime}` : null].filter(Boolean).join(" "),
            totalTokens > 0 ? `${totalTokens.toLocaleString()} tokens` : null,
          ].filter(Boolean).join("  ·  ")}
        </Typography>

        {/* Action / archive button */}
        {onAction && (
          <Tooltip title={isArchived ? "Restore to active" : "Mark actioned & archive"} placement="left">
            <IconButton
              onClick={() => onAction(!isArchived)}
              size="small"
              sx={{
                position: "absolute", right: 74, top: 10,
                color: "rgba(255,255,255,0.6)",
                "&:hover": { color: "white", backgroundColor: "rgba(255,255,255,0.1)" },
              }}
            >
              {isArchived ? <Unarchive fontSize="small" /> : <Archive fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}

        {/* Copy transcript button */}
        <Tooltip title={copied ? "Copied!" : "Copy transcript"} placement="left">
          <IconButton
            onClick={copyTranscript}
            size="small"
            sx={{
              position: "absolute", right: 42, top: 10,
              color: copied ? PH_YELLOW : "rgba(255,255,255,0.6)",
              "&:hover": { color: "white", backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            <ContentCopy fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute", right: 10, top: 10,
            color: "rgba(255,255,255,0.7)",
            "&:hover": { color: "white", backgroundColor: "rgba(255,255,255,0.1)" },
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Summary strip */}
      {hasSummaryContent && (
        <Box sx={{ px: 2.5, py: 1.5, backgroundColor: PH_YELLOW_TINT, borderBottom: `2px solid ${PH_YELLOW_LIGHT}` }}>
          {topics.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: session?.Summary ? 1 : 0 }}>
              {topics.map((t, i) => (
                <Chip key={i} label={t} size="small"
                  sx={{ backgroundColor: PH_YELLOW, color: PH_BLUE, fontWeight: 700, fontSize: 12 }}
                />
              ))}
            </Box>
          )}
          {session?.Summary && (
            <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.65, fontStyle: "italic" }}>
              {session.Summary}
            </Typography>
          )}
        </Box>
      )}

      {/* Linked tickets */}
      {tickets.length > 0 && (
        <Box sx={{ px: 2.5, py: 1, backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, mr: 0.5 }}>Tickets:</Typography>
          {tickets.map((t) => {
            const style = TICKET_STATUS_STYLES[t.Status] || { bg: "#f1f5f9", color: "#475569" };
            return (
              <Chip
                key={t.TicketID}
                label={`#${t.TicketID} · ${TICKET_TYPE_LABELS[t.TicketType] || t.TicketType} · ${t.Status === "resolved" ? "resolved ✓" : t.Status.replace("_", " ")}`}
                size="small"
                sx={{ fontSize: 11, height: 20, fontWeight: 600, backgroundColor: style.bg, color: style.color, "& .MuiChip-label": { px: 1 } }}
              />
            );
          })}
        </Box>
      )}

      {/* Chat area */}
      <DialogContent
        ref={scrollRef}
        sx={{ flex: 1, overflowY: "auto", backgroundColor: "#f4f6fb", p: 2.5 }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress size={32} sx={{ color: PH_YELLOW }} />
          </Box>
        ) : error ? (
          <Typography color="error" align="center" sx={{ mt: 4 }}>{error}</Typography>
        ) : messages.length === 0 ? (
          <Typography align="center" sx={{ color: "#94a3b8", mt: 4 }}>No messages found for this session.</Typography>
        ) : (
          messages.map((msg) => (
            <MessageBubble
              key={msg.MessageID}
              msg={msg}
              staffName={session?.StaffName}
              chemistName={session?.ChemistName}
            />
          ))
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CherubSessionModal;
